import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import WorkTypeFolderTile from '@atom/components/common/workTemplateFolderTree/WorkTypeFolderTile';
import WorkTypeFolderTree from '@atom/components/common/workTemplateFolderTree/WorkTypeFolderTree';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { WORK_TEMPLATE_UPDATE } from '@atom/graph/workTemplate';
import {
  Icon,
  IconButton,
  Modal,
  Snackbar,
  Switch,
  TextField,
} from '@atom/mui';
import { ArrowBackIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { PolicyAction } from '@atom/types/policy';
import { WorkOrderType } from '@atom/types/work';
import { WorkTemplateFolderTree } from '@atom/types/workTemplate';
import api from '@atom/utilities/api';
import { WORK_TEMPLATES_FOLDER_TREE_ENDPOINT } from '@atom/utilities/endpoints';
import { getErrorCode } from '@atom/utilities/validationUtilities';
import { getWorkTemplateFolderByFolderPath } from '@atom/utilities/workTemplateUtilities';

import WorkTemplateSchemaSelect from './WorkTemplateSchemaSelect';
import WorkTemplateTypeSelect from './WorkTemplateTypeSelect';

import classes from './workTypeEditModal.module.css';

const HTTP_RESOURCE_CONFLICT = 409;
const EMPTY_SCHEMA = 'empty';

enum View {
  MAIN = 'MAIN',
  FOLDER_TREE = 'FOLDER_TREE',
  CONFIRM = 'CONFIRM',
}

const styles = {
  textFieldStyle: {
    marginLeft: '1rem',
    color: colors.neutral.dark,
  },
  icon: {
    marginTop: '1rem',
  },
  modalContent: {
    padding: 0,
  },
  back: {
    marginRight: '1rem',
  },
};

interface Props {
  open: boolean;
  closeModal: () => void;
}

const WorkTypeEditModal = ({ open, closeModal }: Props) => {
  const { workTemplate, refetch } = useContext(WorkTemplateContext);

  const [name, setName] = useState<string>(workTemplate?.name);
  const [error, setError] = useState<string>('');
  const [activeView, setActiveView] = useState<View>(View.MAIN);
  const [schemaId, setSchemaId] = useState<string>(
    workTemplate?.schemaId || EMPTY_SCHEMA,
  );
  const [type, setType] = useState<WorkOrderType>(
    R.pathOr(WorkOrderType.DEFAULT, ['type'], workTemplate),
  );
  const [appointmentsEnabled, setAppointmentsEnabled] = useState<boolean>(
    workTemplate?.appointmentsEnabled || false,
  );
  const [workTemplateFolder, setWorkTemplateFolder] =
    useState<WorkTemplateFolderTree>();
  const [loadingTree, setLoadingTree] = useState<boolean>(true);
  const [workTemplateFolderTree, setWorkTemplateFolderTree] =
    // @ts-ignore
    useState<WorkTemplateFolderTree>({});

  const [workTemplateUpdate, { loading: editLoading }] =
    useMutation(WORK_TEMPLATE_UPDATE);

  useEffect(() => {
    const getWorkTemplateFolderTree = async () => {
      setLoadingTree(true);

      const { data } = await api.get(
        `${WORK_TEMPLATES_FOLDER_TREE_ENDPOINT}?action=${PolicyAction.READ}`,
      );

      setWorkTemplateFolderTree(data);
      setLoadingTree(false);
    };

    getWorkTemplateFolderTree();
  }, []);

  const selectedWorkTemplateFolder = useMemo(() => {
    return getWorkTemplateFolderByFolderPath(
      workTemplateFolderTree,
      workTemplate.folderPath,
    );
  }, [workTemplateFolderTree, workTemplate]);

  useEffect(() => {
    setWorkTemplateFolder(selectedWorkTemplateFolder);
  }, [selectedWorkTemplateFolder]);

  useEffect(() => {
    setName(workTemplate?.name);
    setSchemaId(workTemplate?.schemaId || EMPTY_SCHEMA);
    setWorkTemplateFolder(selectedWorkTemplateFolder);
    setActiveView(View.MAIN);
    setType(R.pathOr(WorkOrderType.DEFAULT, ['type'], workTemplate));
    setAppointmentsEnabled(workTemplate?.appointmentsEnabled || false);
    setError('');
  }, [workTemplate, open]);

  const confirm = async () => {
    try {
      const selectedSchemaId =
        type === WorkOrderType.DEFAULT
          ? schemaId !== EMPTY_SCHEMA
            ? schemaId
            : ''
          : '';

      await workTemplateUpdate({
        variables: {
          input: {
            workOrderTemplateId: workTemplate.id,
            folderId: workTemplateFolder.id,
            type,
            appointmentsEnabled,
            ...(name !== workTemplate.name && { name }),
            ...(!R.isNil(selectedSchemaId) && { schemaId: selectedSchemaId }),
          },
        },
      });

      refetch();
      closeModal();
    } catch (err) {
      if (getErrorCode(err) === HTTP_RESOURCE_CONFLICT) {
        setError('This name is already in use.');
      } else {
        Snackbar.error({ message: 'An unknown error occurred' });
        closeModal();
      }
    }
  };

  const onConfirm = () => {
    if (
      activeView !== View.CONFIRM &&
      R.pathOr(WorkOrderType.DEFAULT, ['type'], workTemplate) !== type
    ) {
      setActiveView(View.CONFIRM);
    } else {
      confirm();
    }
  };

  const handleNameChange = event => {
    setName(event.target.value);
    setError('');
  };

  const isDisabled =
    R.isEmpty(name) || R.isNil(workTemplateFolder) || workTemplate.published;

  const getTitle = (): React.ReactNode => {
    const titles = {
      [View.MAIN]: 'Edit Work Template',
      [View.FOLDER_TREE]: (
        <>
          <IconButton
            style={styles.back}
            onClick={() => setActiveView(View.MAIN)}
            size="small"
          >
            <Icon icon={ArrowBackIcon} />
          </IconButton>
          Choose Folder
        </>
      ),
      [View.CONFIRM]: 'Change Work Template Type?',
    };

    return titles[activeView];
  };

  const chooseFolderOnClick = workTemplate.published
    ? () => {}
    : () => setActiveView(View.FOLDER_TREE);

  const confirmButtonText = activeView === View.CONFIRM ? 'Confirm' : 'Save';

  const singleToMultiText =
    'Changing the template type will remove the asset type and forms of the same asset type from all tasks. Are you sure?';

  const multiToSingleText =
    'Changing the template type will remove all the changes you made to the Locations and Assets sections. Are you sure?';

  const switchLabel = (
    <div>
      <div className={classes.labelTitle}>Enable Appointments</div>
      <div className={classes.labelSubtext}>
        {appointmentsEnabled
          ? 'When enabled, Planned Start Date field will appear and require date and time. Due Date will require date and time.'
          : 'When disabled, only Due Date will be shown on work. Due Date is optional and does not require time.'}
      </div>
    </div>
  );

  const getContent = () => {
    const content = {
      [View.MAIN]: (
        <div className={classes.workTypeCreationModalContent}>
          <div className={classes.sectionTitle}>Details</div>
          <div className={classes.modalRow}>
            <img style={styles.icon} src={renameIcon} />
            <TextField
              id="name"
              label="Name *"
              value={name}
              onChange={handleNameChange}
              style={styles.textFieldStyle}
              error={!!error}
              helperText={error}
              disabled={workTemplate.published}
            />
          </div>
          <div className={classes.modalRow}>
            <Icon color={colors.neutral.gray} icon={FolderIcon} />
            {!R.isNil(workTemplateFolder) ? (
              <div className={classes.tileContainer}>
                <WorkTypeFolderTile
                  workTemplateFolder={workTemplateFolder}
                  removeWorkTemplateFolder={() => setWorkTemplateFolder(null)}
                  disabled={workTemplate.published}
                />
              </div>
            ) : (
              <div
                className={classes.chooseButton}
                onClick={chooseFolderOnClick}
              >
                Choose Folder *
              </div>
            )}
          </div>
          <div className={classes.modalRow}>
            <Switch
              checked={appointmentsEnabled}
              onChange={() => setAppointmentsEnabled(!appointmentsEnabled)}
              label={switchLabel}
              disabled={workTemplate.published}
            />
          </div>
          <WorkTemplateTypeSelect type={type} setType={setType} />
          {type === WorkOrderType.DEFAULT && (
            <WorkTemplateSchemaSelect
              schemaId={schemaId}
              setSchemaId={setSchemaId}
            />
          )}
        </div>
      ),
      [View.FOLDER_TREE]: (
        <WorkTypeFolderTree
          setWorkTemplateFolder={setWorkTemplateFolder}
          setActiveView={() => setActiveView(View.MAIN)}
        />
      ),
      [View.CONFIRM]: (
        <div className={classes.confirmText}>
          {type === WorkOrderType.DEFAULT
            ? multiToSingleText
            : singleToMultiText}
        </div>
      ),
    };

    return content[activeView];
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={() => closeModal()}
      onConfirm={() => onConfirm()}
      confirmButtonText={confirmButtonText}
      loading={editLoading || loadingTree}
      contentStyle={styles.modalContent}
      disabled={isDisabled}
    >
      {getContent()}
    </Modal>
  );
};

export default WorkTypeEditModal;
